import type { Page } from '@hello-aria/makalu-api-client'
import type { InfiniteData, UseInfiniteQueryResult, UseMutationResult } from '@tanstack/react-query'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { usersTableFields } from '@/modules/users/enums/usersEnums'
import type { QueryParams } from '@/shared/models/queryModels'

import { In } from '@hello-aria/query-string'

import { deleteUser, listUsers, createUser } from '@/modules/users/services/userServices'
import type {
    ComparisonFilterInput,
    NestedLogicalFilterInput,
    QueryStringInput
} from '@hello-aria/query-string/dist/stringify'
import type { HooksProps } from '@/shared/models/rootModel'
import type { DeleteUser, PostUser, User } from '@/shared/models/userModel'
import { listOnboardings } from '@/shared/services/onboardingServices'
import { getPageSize, isErrorWithCode, mapQuery } from '@/shared/utils/queryUtils'
import { resolveUserFilters } from '@/shared/utils/usersUtils'
import { useMe } from './useMe'
import { useQueryParamsState } from './useQueryParams'
import { useDeprecatedSetToast, stringToToasterKeyValidator } from './useSetToast'

export const usersQueryKeys = {
    root: ['infiniteUsers'],
    users: ['users'],
    infiniteUsers: (companyId: string, query?: QueryStringInput): (string | undefined | QueryStringInput)[] => [
        ...usersQueryKeys.root,
        companyId,
        query
    ],
    deprecated_infiniteUsers: (companyId: string, queryParams?: QueryParams): (string | QueryParams | undefined)[] => [
        ...usersQueryKeys.root,
        companyId,
        queryParams
    ]
}

interface UseInfiniteUsers {
    query?: QueryStringInput
    enabled?: boolean
}

export const useInfiniteUsers = (
    options?: UseInfiniteUsers
): UseInfiniteQueryResult<InfiniteData<Page<User> | undefined>> => {
    const { query, enabled } = options || {}
    const { companyId } = useMe()

    return useInfiniteQuery({
        queryKey: usersQueryKeys.infiniteUsers(companyId, query),
        queryFn: async ({ pageParam }: { pageParam?: string }) => {
            return listUsers({
                companyId,
                query: {
                    ...query,
                    pagination: {
                        after: pageParam,
                        first: getPageSize(query)
                    }
                }
            })
        },
        enabled,
        getNextPageParam: (lastPage) => {
            if (lastPage?.pagination?.hasNextPage) return lastPage?.pagination?.endCursor
            return undefined
        },

        initialPageParam: undefined
    })
}

interface deprecated_UseInfiniteUsers {
    customSearch?: string
    customFilters?: (ComparisonFilterInput | NestedLogicalFilterInput)[]
    enabled?: boolean
}

export const useDeprecatedInfiniteUsers = (options?: deprecated_UseInfiniteUsers) => {
    const { customSearch, customFilters, enabled = true } = options || {}
    const { companyId } = useMe()
    const queryParamsState = useQueryParamsState()
    const filters: (ComparisonFilterInput | NestedLogicalFilterInput)[] = customFilters ?? []

    const searchValue = customSearch ?? queryParamsState.get('users_search')

    return useInfiniteQuery({
        queryKey: usersQueryKeys.deprecated_infiniteUsers(companyId, {
            ...queryParamsState.pick([`users_sort`, `users_filter`]),
            users_search: searchValue
        }),

        queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
            const users = await listUsers({
                companyId,
                query: mapQuery({
                    search: { value: searchValue, path: usersTableFields },
                    sortBy: queryParamsState.get('users_sort'),
                    pagination: { after: pageParam },
                    filter: resolveUserFilters({
                        customFilters: [...filters, ...(queryParamsState.get('users_filter') ?? [])]
                    })
                })
            })
            const relatedOnboardingFilters = In(
                'targetId',
                users?.body?.map((user) => user.id)
            )
            const relatedOnboardings = await listOnboardings({
                companyId,
                query: mapQuery({
                    filter: relatedOnboardingFilters
                })
            })
            const body = users?.body?.map((user): User => {
                const onboarding = relatedOnboardings?.body?.find((onboarding) => onboarding.targetId === user.id)
                return { ...user, onboarding }
            })
            return { ...users, body }
        },

        enabled,

        getNextPageParam: (lastPage) => {
            if (lastPage?.pagination?.hasNextPage) return lastPage?.pagination?.endCursor
            return undefined
        },

        initialPageParam: undefined
    })
}

export const usePostUser = ({
    shouldSetToaster
}: HooksProps): UseMutationResult<User | undefined, unknown, PostUser> => {
    const queryClient = useQueryClient()
    const { companyId } = useMe()

    const setToast = useDeprecatedSetToast()

    return useMutation({
        mutationFn: (payload: PostUser) => createUser(payload, companyId),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: usersQueryKeys.root })
            if (shouldSetToaster) setToast('createUser', 'positive')
        },
        onError: (error: unknown) => {
            const toasterKey =
                (isErrorWithCode(error) ? stringToToasterKeyValidator(error.code) : undefined) ?? 'createUser'
            if (shouldSetToaster) setToast(toasterKey, 'negative')
        }
    })
}

export const useDeleteUser = (): UseMutationResult<User | undefined, unknown, DeleteUser> => {
    const { companyId } = useMe()

    return useMutation({
        mutationFn: (payload: DeleteUser) => deleteUser(companyId, payload)
    })
}
